import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setUser } from '../api/userService';
import { toast } from '../utils/toasts';

/**
 * Hook para actualizar usuario
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setUser,
    onMutate: async (updatedUser) => {
      // Cancelar queries en curso
      await queryClient.cancelQueries({ queryKey: ['users'] });
      await queryClient.cancelQueries({ queryKey: ['user', updatedUser.id] });
      
      // Snapshot del valor anterior
      const previousUsers = queryClient.getQueryData({ queryKey: ['users'] });
      const previousUser = queryClient.getQueryData({ queryKey: ['user', updatedUser.id] });
      
      // Optimistic update en la lista de usuarios
      queryClient.setQueryData({ queryKey: ['users'] }, (old) => {
        if (!old?.data?.results) return old;
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.map(user => 
              user.id === updatedUser.id ? { ...user, ...updatedUser } : user
            ),
          },
        };
      });
      
      // Optimistic update en el usuario individual
      queryClient.setQueryData({ queryKey: ['user', updatedUser.id] }, (old) => {
        if (!old) return old;
        return { ...old, ...updatedUser };
      });
      
      return { previousUsers, previousUser };
    },
    onSuccess: (data) => {
      toast('success', 'Usuario actualizado exitosamente');
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error, updatedUser, context) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al actualizar usuario';
      toast('error', errorMessage);
      console.error('Error updating user:', error);
      
      // Revertir optimistic updates en caso de error
      if (context?.previousUsers) {
        queryClient.setQueryData({ queryKey: ['users'] }, context.previousUsers);
      }
      if (context?.previousUser) {
        queryClient.setQueryData({ queryKey: ['user', updatedUser.id] }, context.previousUser);
      }
    },
    onSettled: () => {
      // Siempre refetch para asegurar sincronización
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    retry: 1,
    retryDelay: 1000,
  });
} 